import {
  DeepInsightsEnabled,
  DeepInsightSummary,
  DeepInsightWithContent,
} from "@/types/deep-insights.ts";
import { SidebarSettings } from "@/types/user";
import {
  ReportConfigV2,
  ReportConfigWithOptionalCategory,
} from "@/common/report-configuration";
import { LOGOUT_URL } from "@/constants/api";
import type { CustomWorkspace } from "@/types/customWorkspace";
import { Report } from "@/common/report-configuration";
import {
  Deck,
  DeckForCreateModal,
  DeckForDB,
  hydrateDeck,
} from "@/common/decks";
import { SystemPromptData } from "@/common/system-prompts";
import {
  StrategicInitiativeData,
  StrategicInitiativeRequest,
} from "@/common/strategic-initiatives.ts";
import { DatasetName } from "@/common/constants";
import { FilterName } from "@/common/filters";
import mixpanel from "@/lib/mixpanel";

/* eslint-disable @typescript-eslint/no-explicit-any */

// Stub for authentication
let authToken: string | null = null;

const getAuthToken = () => {
  return authToken;
};

export const setAuthToken = (token: string) => {
  authToken = token;
  localStorage.setItem("authToken", token);
};

export const pingServer = async () => {
  if (!authToken) {
    // console.log("No auth token, skipping ping");
    return false;
  }

  try {
    // console.log("Pinging server...");
    await post("/ping");
    // console.log("Ping successful");
    return true;
  } catch (error) {
    // Ping failed - could be network issue or session expired
    // The error handler in apiRequest will have already checked for 401/403
    console.error("Error pinging server:", error);
    return false;
  }
};

const API_BASE_URL = process.env.PARCEL_API_BASE_URL || "http://localhost:3003";

const apiRequest = async (
  method: string,
  endpoint: string,
  data: any = null,
  retryCount: number = 0,
) => {
  const token = await getAuthToken();
  const headers = {
    "Content-Type": "application/json",
    Authorization: token || "",
  };

  const config = {
    method,
    headers,
    body: data ? JSON.stringify(data) : undefined,
  };

  let response: Response;
  try {
    response = await fetch(`${API_BASE_URL}${endpoint}`, config as RequestInit);
  } catch (fetchError) {
    // Network error - if this is after tab suspension, retry once
    if (retryCount === 0 && endpoint !== "/ping") {
      console.log("Network error, retrying request after delay...");
      await new Promise((resolve) => setTimeout(resolve, 1000));
      return apiRequest(method, endpoint, data, retryCount + 1);
    }
    throw fetchError;
  }

  if (response.status === 401 || response.status === 403) {
    // Session expired or forbidden
    // Only redirect to logout if we're sure the session is invalid
    // (not just a transient network issue)
    if (retryCount === 0 && endpoint !== "/ping") {
      // Retry once after a brief delay to handle tab suspension recovery
      console.log("Auth failed, attempting recovery after delay...");
      await new Promise((resolve) => setTimeout(resolve, 1000));
      return apiRequest(method, endpoint, data, retryCount + 1);
    }

    window.location.href = LOGOUT_URL;
    throw new Error("Authentication required");
  }

  if (!response.ok) {
    throw new Error(`API request failed: ${response.statusText}`);
  }

  if (response.status === 204) {
    // No content response, return empty object
    return {};
  }

  return response.json();
};

// Modify the existing HTTP method helpers to support streaming
const get = (endpoint: string) => apiRequest("GET", endpoint);
export const post = (endpoint: string, data?: any) =>
  apiRequest("POST", endpoint, data);
const put = (endpoint: string, data?: any) => apiRequest("PUT", endpoint, data);
const patch = (endpoint: string, data?: any) =>
  apiRequest("PATCH", endpoint, data);
const del = (endpoint: string) => apiRequest("DELETE", endpoint);

/* ------------- ENDPOINTS ------------- */

export const createChat = (type?: string, insightId?: string) =>
  post("/createChat", { type, insightId });

export const getChat = (id: string) => get(`/chat/${id}`);

export const submitMessage = (
  userQuery: string,
  chatId: string,
  accountId: string,
  type?: string,
) => post("/submitMessage", { userQuery, chatId, accountId, type });

export const signOut = () => post("/parita/sign_out", {});

export const updateMessageFeedback = (messageId: number, feedback: number) =>
  post(`/message/${messageId}/feedback`, { feedback });

export const updateUserSidebarSettings = (settings: SidebarSettings) =>
  post(`/user/current/sidebarSettings`, settings);

export const getChats = (
  limit: number = 10,
  query: string = "",
  offset: number = 0,
  type?: string,
  accountId?: string,
) => {
  const params = new URLSearchParams();
  params.append("limit", limit.toString());
  params.append("query", query);
  params.append("offset", offset.toString());
  if (type) params.append("type", type);
  if (accountId) params.append("accountId", accountId);
  return get(`/chats?${params.toString()}`);
};

export const updateChatPinned = (chatId: string, pinned: boolean) =>
  put(`/chats/${chatId}/pin`, { pinned });

export const getCurrentUser = () => get("/user/current");

export const getReportDataV2 = (config: ReportConfigV2) =>
  post("/areportData", { config });

export const saveReportV2 = (id: string, config: ReportConfigV2) => {
  mixpanel.track("Save Report", {
    eventDescription: config.title || "Untitled Report",
    reportId: id,
  });
  return put(`/report/${id}`, { config }); // Uses the same endpoint as v1
};

export const patchReport = (
  id: string,
  config: Partial<ReportConfigWithOptionalCategory>,
) => patch(`/report/${id}`, { config });

export const createReportV2 = (config: ReportConfigV2) => {
  mixpanel.track("Create Report", {
    eventDescription: config.title || "Untitled Report",
  });
  return post("/areport", { config });
};

export const getReports = async (
  limit?: number,
  query?: string,
  offset?: number,
  includeSystem: boolean = false,
  accountId?: string,
) => {
  const params = new URLSearchParams();
  if (limit) params.append("limit", limit.toString());
  if (query) params.append("query", query);
  if (offset) params.append("offset", offset.toString());
  if (includeSystem) params.append("include_system", "true");
  if (accountId) params.append("accountId", accountId);
  const result = await get(`/reports?${params.toString()}`);
  // parse the created_at and updated_at fields to Date objects
  result.forEach((report: Report) => {
    if (report.created_at) {
      report.created_at = new Date(report.created_at);
    }
    if (report.updated_at) {
      report.updated_at = new Date(report.updated_at);
    }
  });
  return result;
};

export const getRefreshedAiSummaryV2 = (
  config: ReportConfigV2,
  data: any[],
) => {
  return post(`/areport/summary/regen`, { config, data });
};

export const getReportV2 = (id: string) => get(`/areport/${id}`);

export const getDatasetFieldOptions = (
  datasetName: DatasetName,
  filterDefinitionName: FilterName,
) => post("/dataset/field/options", { datasetName, filterDefinitionName });

export const authenticate = async (auth_nonce: string) => {
  try {
    // Store workspace parameter from URL if present
    const urlParams = new URLSearchParams(window.location.search);
    const workspaceParam = urlParams.get("wksp");
    if (!workspaceParam) {
      throw new Error("Workspace parameter not found in URL");
    }
    try {
      localStorage.setItem("userLogin", "true");
    } catch (storageError) {
      console.warn("Failed to store user login:", storageError);
      // Continue with authentication even if storage fails
    }

    const response = await fetch(`${API_BASE_URL}/auth`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ auth_nonce, workspace: workspaceParam }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || "Authentication failed");
    }

    const { token } = await response.json();
    setAuthToken(token);
    return token;
  } catch (error) {
    console.error("Authentication error:", error);
    throw error;
  }
};

export const renameItem = (
  type: "reports" | "chats" | "dashboards" | "workspaces",
  id: string,
  newTitle: string,
) => put(`/${type}/${id}/title`, { title: newTitle });

export const deleteItem = (
  type: "reports" | "chats" | "dashboards" | "workspaces",
  id: string,
) => del(`/${type}/${id}`);

export const duplicateItem = (
  type: "reports" | "chats" | "dashboards" | "workspaces",
  id: string,
) => post(`/${type}/${id}/duplicate`);

export const getChatSuggestions = (chatId?: string) =>
  post(`/chat/suggestions`, { chatId });

export const modifyReportByQuery = (
  config: ReportConfigV2,
  query: string,
  reportId?: string,
) => post("/modifyReportByQuery", { config, query, reportId });

export const saveCustomWorkspace = (workspace: CustomWorkspace) => {
  const { id, ...workspaceData } = workspace;
  if (id) {
    return patch(`/workspaces/${id}`, workspaceData);
    // create new workspace
  } else {
    return post("/workspaces", workspaceData);
  }
};

export const getCustomWorkspace = (id: string) => get(`/workspaces/${id}`);

export const getCustomWorkspaces = (
  limit?: number,
  query?: string,
  offset?: number,
  sort?: "recent" | "alpha",
  accountId?: string,
) => {
  const params = new URLSearchParams();
  if (limit) params.append("limit", limit.toString());
  if (query) params.append("query", query);
  if (offset) params.append("offset", offset.toString());
  if (sort) params.append("sort", sort);
  if (accountId) params.append("accountId", accountId);
  return get(`/workspaces?${params.toString()}`);
};

export const getCustomWorkspaceReports = (id: string) =>
  get(`/workspaces/${id}/reports`);

// =============================================================================
// SYSTEM WORKSPACE API (Generic)
// =============================================================================

export const getSystemWorkspaceReports = (workspaceId: string) =>
  get(`/system-workspace/${workspaceId}/reports`);

export const addSystemWorkspaceReport = (
  workspaceId: string,
  reportId: string,
) => post(`/system-workspace/${workspaceId}/report`, { reportId });

export const removeSystemWorkspaceReport = (
  workspaceId: string,
  reportId: string,
) =>
  apiRequest("DELETE", `/system-workspace/${workspaceId}/report/${reportId}`);

// =============================================================================

export const createReportSnapshotV2 = (
  reportId: string,
  config: ReportConfigV2,
  data: any,
  title?: string,
) =>
  post(`/report/${reportId}/createSnapshot`, {
    config,
    data,
    title,
  });

export const getReportSnapshotMetadata = (reportId: string) =>
  get(`/report/${reportId}/snapshots/metadata`);

export const getReportSnapshotV2 = (reportId: string, snapshotId: string) =>
  get(`/areport/${reportId}/snapshots/${snapshotId}`);

export const getDeepInsightsSummary = (): Promise<DeepInsightSummary[]> =>
  get(`/deep-insights/summary`);

export const getDeepInsights = (): Promise<DeepInsightSummary[]> =>
  get(`/deep-insights/all`);

export const toggleDeepInsightPin = (id: string): Promise<boolean> =>
  put(`/deep-insights/${encodeURIComponent(id)}/toggle-pin`, {});

export const toggleDeepInsightArchive = (id: string): Promise<boolean> =>
  put(`/deep-insights/${encodeURIComponent(id)}/toggle-archive`, {});

export const getSystemPrompts = (): Promise<SystemPromptData[]> =>
  get(`/system-prompts`);

export const updateSystemPrompts = (
  id: string,
  prompt: string,
): Promise<SystemPromptData> =>
  put(`/system-prompts/${encodeURIComponent(id)}`, { prompt });

export const getStrategicInitiatives = (): Promise<StrategicInitiativeData[]> =>
  get(`/strategic-initiatives`);

export const createStrategicInitiative = (
  request: StrategicInitiativeRequest,
): Promise<boolean> => post(`/strategic-initiatives`, request);

export const updateStrategicInitiative = (
  id: string,
  request: StrategicInitiativeRequest,
): Promise<boolean> =>
  put(`/strategic-initiatives/${encodeURIComponent(id)}`, request);

export const deleteStrategicInitiative = (id: string): Promise<boolean> =>
  del(`/strategic-initiatives/${encodeURIComponent(id)}`);

export const getDeepInsightDetails = async (
  id: string,
): Promise<DeepInsightWithContent | null> => {
  const response = await get(`/deep-insights/${encodeURIComponent(id)}`);
  response.content.sections = [];

  if (typeof response.content.sectionsJSONString === "string") {
    try {
      const sections: any[] = JSON.parse(response.content.sectionsJSONString);

      response.content.sections = sections.map((section) => ({
        title: section.section_title,
        content: section.section_content,
        data: section.section_data,
      }));
    } catch (e) {
      console.log(e);
      response.content.sections = [];
    }
  }

  if (typeof response.content.chartJSONString === "string") {
    try {
      response.content.chart = JSON.parse(response.content.chartJSONString);
    } catch (e) {
      console.log(e);
    }
  }

  return response;
};

export const getDeepInsightsEnabled = (): Promise<DeepInsightsEnabled> =>
  get(`/deep-insights-enabled`);

export const getDecks = async (
  query: string,
  pinned: boolean,
): Promise<Deck[]> => {
  const params = new URLSearchParams();
  if (pinned) params.append("pinned", "1");
  if (query) params.append("query", query);
  const decks = await get(`/decks?${params.toString()}`);
  return decks.map((deck: DeckForDB) => hydrateDeck(deck));
};

export const getDeck = async (id: string): Promise<Deck> => {
  const deck: DeckForDB = await get(`/deck/${id}`);
  return hydrateDeck(deck);
};

export const createDeck = (deck: Partial<DeckForCreateModal>) =>
  post("/deck", {
    ...deck,
  });

export const updateDeck = async (deck: Deck) => {
  const response: { deck: DeckForDB; changed_slides: string[] } = await put(
    `/deck/${deck.id}`,
    {
      ...deck,
    },
  );
  return {
    deck: hydrateDeck(response.deck),
    changed_slides: response.changed_slides || [],
  };
};

export const deleteDeck = (id: string) => del(`/deck/${id}`);

export const setUserOnboarded = () => post("/user/current/onboarded", {});

export const getCustomInstanceMetadata = () => get("/data/custom");
